import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, InputNumber, Input, Button, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useCurrency } from '../../hooks/useCurrency';
import { CashRegisterDto } from '../../types/cashRegister';

const { TextArea } = Input;
const { Text } = Typography;

interface AddCashMovementModalProps {
  visible: boolean;
  movementType: 'income' | 'expense' | null;
  onCancel: () => void;
  onSubmit: (values: { amount: number; reason: string; reference?: string; type: string }) => void;
  onSubmitAndPrint?: (values: { amount: number; reason: string; reference?: string; type: string }) => void;
  loading?: boolean;
  /** Caja afectada: alimenta el meta del título y el hint "quedarán $X en la caja" */
  cashRegister?: CashRegisterDto | null;
}

const AddCashMovementModal: React.FC<AddCashMovementModalProps> = ({
  visible,
  movementType,
  onCancel,
  onSubmit,
  onSubmitAndPrint,
  loading = false,
  cashRegister,
}) => {
  const [form] = Form.useForm();
  const { currencySymbol } = useCurrency();
  const [selectedReason, setSelectedReason] = useState<string>('');
  // Para el hint de saldo: refleja el monto tecleado en vivo
  const amountWatch = Form.useWatch('amount', form);

  // Submit guard: blocks the double-click race where the user taps a submit
  // button twice before the parent's RTK Query `loading` flag flips to true.
  // - Set synchronously inside the click handler (before any await), so the
  //   second tap arrives with `submitInFlight.current === true` and is ignored.
  // - Mirrored into local state so the buttons can be visibly disabled too.
  // - Reset whenever the modal is re-opened, or when the parent's loading
  //   state finishes (success → modal closes via parent; error → user can retry).
  const submitInFlight = useRef(false);
  const [localSubmitting, setLocalSubmitting] = useState(false);

  // Reset form when modal opens/closes or movement type changes
  useEffect(() => {
    if (visible && movementType) {
      form.resetFields();
      setSelectedReason('');
      submitInFlight.current = false;
      setLocalSubmitting(false);
    }
  }, [visible, movementType, form]);

  // When the parent's mutation finishes (loading goes back to false), release
  // the local guard so the user can retry on error without re-opening the modal.
  useEffect(() => {
    if (!loading && submitInFlight.current) {
      submitInFlight.current = false;
      setLocalSubmitting(false);
    }
  }, [loading]);

  const getTitle = () => {
    if (movementType === 'income') {
      return 'Ingreso a caja';
    }
    return 'Retiro de caja';
  };

  // Los `value` se conservan idénticos: son lo que el backend almacena como reason.
  // Solo los labels se acortan para caber como pills de un tap.
  const getReasonOptions = () => {
    if (movementType === 'income') {
      return [
        { value: 'Ingreso por fondo inicial adicional', label: 'Fondo adicional' },
        { value: 'Ingreso por devolución', label: 'Devolución' },
        { value: 'Ingreso por ajuste', label: 'Ajuste' },
        { value: 'Ingreso por corrección de error', label: 'Corrección' },
        { value: 'Ingreso por préstamo', label: 'Préstamo' },
        { value: 'otro', label: 'Otro…' },
      ];
    }
    return [
      { value: 'Retiro para depósito bancario', label: 'Depósito bancario' },
      { value: 'Retiro para cambio', label: 'Cambio' },
      { value: 'Retiro por pago de proveedor', label: 'Pago a proveedor' },
      { value: 'Retiro por gastos operativos', label: 'Gastos operativos' },
      { value: 'Retiro por pago de servicios', label: 'Servicios' },
      { value: 'Retiro por compra de insumos', label: 'Insumos' },
      { value: 'otro', label: 'Otro…' },
    ];
  };

  const handleSubmit = async () => {
    // Synchronous guard against the double-click race. RTK Query's `loading`
    // doesn't flip to true until the mutation actually starts, so the gap
    // around `await form.validateFields()` is enough for a second tap to
    // squeeze through. Setting the ref FIRST (before any await) closes that
    // window. See: duplicate "nomina" cash movements reported by Mirador.
    if (submitInFlight.current || loading) {
      return;
    }
    submitInFlight.current = true;
    setLocalSubmitting(true);

    try {
      const values = await form.validateFields();

      // Determine the cash movement type based on movementType
      // CORRECTION: Adds to cash (income/deposit)
      // DROP: Removes from cash (withdrawal for bank deposit, reducing cash in register)
      const cashMovementType = movementType === 'income' ? 'CORRECTION' : 'DROP';

      onSubmit({
        amount: values.amount,
        reason: selectedReason === 'otro' ? values.customReason : values.reason,
        reference: values.reference,
        type: cashMovementType,
      });
      // Note: do NOT release the guard here. The parent owns the lifecycle:
      // on success it closes the modal (which resets the guard via useEffect);
      // on error its `loading` flips back to false and the guard releases.
    } catch (error) {
      // Validation failed → release the guard so the user can correct & retry.
      submitInFlight.current = false;
      setLocalSubmitting(false);
      console.error('Validation failed:', error);
    }
  };

  const handleSubmitAndPrint = async () => {
    // Same guard as handleSubmit — both buttons share the same in-flight latch
    // so alternating taps between "Registrar" and "Registrar e Imprimir" can't
    // produce two requests either.
    if (submitInFlight.current || loading) {
      return;
    }
    submitInFlight.current = true;
    setLocalSubmitting(true);

    try {
      const values = await form.validateFields();
      const cashMovementType = movementType === 'income' ? 'CORRECTION' : 'DROP';

      onSubmitAndPrint?.({
        amount: values.amount,
        reason: selectedReason === 'otro' ? values.customReason : values.reason,
        reference: values.reference,
        type: cashMovementType,
      });
    } catch (error) {
      submitInFlight.current = false;
      setLocalSubmitting(false);
      console.error('Validation failed:', error);
    }
  };

  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    form.setFieldsValue({ reason: value });
  };

  // While a submit is in flight, lock down every dismissal vector — not just
  // the explicit Cancel button — so the user can't close the modal mid-POST
  // via the X icon, ESC key, or mask click. Otherwise the parent would
  // unmount the modal while the network request is still in the air, leaving
  // the disabled-Cancelar intent inconsistent.
  const isBusy = loading || localSubmitting;

  // Efecto del movimiento sobre el efectivo esperado, en vivo
  const expected = cashRegister?.expectedAmount;
  const amountNumber = typeof amountWatch === 'number' ? amountWatch : 0;
  const resultingBalance =
    expected !== undefined && amountNumber > 0
      ? movementType === 'income'
        ? expected + amountNumber
        : expected - amountNumber
      : null;

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, paddingRight: 28 }}>
          <span>{getTitle()}</span>
          {cashRegister?.userName && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              caja de {cashRegister.userName}
            </Text>
          )}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      closable={!isBusy}
      maskClosable={!isBusy}
      keyboard={!isBusy}
      footer={
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button type="text" onClick={onCancel} disabled={isBusy} style={{ marginRight: 'auto' }}>
            Cancelar
          </Button>
          <Button loading={isBusy} disabled={isBusy} onClick={handleSubmit}>
            Registrar
          </Button>
          {onSubmitAndPrint && (
            <Button
              type="primary"
              loading={isBusy}
              disabled={isBusy}
              onClick={handleSubmitAndPrint}
              icon={<PrinterOutlined />}
            >
              Registrar e imprimir
            </Button>
          )}
        </div>
      }
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        <Form.Item
          name="amount"
          label={<Text strong>Monto</Text>}
          rules={[
            { required: true, message: 'El monto es obligatorio' },
            {
              type: 'number',
              min: 0.01,
              message: 'El monto debe ser mayor a 0'
            },
          ]}
        >
          <InputNumber
            style={{
              width: '100%',
              fontFamily: 'ui-monospace, Consolas, monospace',
              fontSize: 18,
              fontWeight: 650,
            }}
            placeholder="0.00"
            precision={2}
            min={0.01}
            prefix={currencySymbol}
            size="large"
            autoFocus
          />
        </Form.Item>

        {/* Motivo como pills de un tap; el value completo (lo que guarda el
            backend) no cambia, solo el label. Las pills escriben al form vía
            handleReasonChange; el Form.Item con Input oculto conserva la
            validación required y su mensaje de error. */}
        <div style={{ marginBottom: 0 }}>
          <div style={{ marginBottom: 4 }}>
            <Text strong>Motivo</Text>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
            {getReasonOptions().map((option) => {
              const selected = selectedReason === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleReasonChange(option.value)}
                  style={{
                    border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--border-color)'}`,
                    borderRadius: 999,
                    background: selected
                      ? 'color-mix(in srgb, var(--color-primary) 12%, var(--bg-primary))'
                      : 'var(--bg-primary)',
                    color: selected ? 'var(--color-primary)' : 'var(--text-secondary)',
                    padding: '5px 14px',
                    fontSize: 13,
                    fontWeight: selected ? 600 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <Form.Item
            name="reason"
            rules={[{ required: true, message: 'Selecciona un motivo' }]}
            style={{ marginBottom: 12 }}
          >
            <Input style={{ display: 'none' }} />
          </Form.Item>
        </div>

        {selectedReason === 'otro' && (
          <Form.Item
            name="customReason"
            label={<Text strong>Especificar motivo</Text>}
            rules={[
              { required: true, message: 'Debes especificar el motivo' },
              { min: 5, message: 'El motivo debe tener al menos 5 caracteres' },
            ]}
          >
            <TextArea
              rows={2}
              placeholder="Describe el motivo del movimiento..."
              maxLength={200}
              showCount
            />
          </Form.Item>
        )}

        <Form.Item
          name="reference"
          label={<Text>Referencia (opcional)</Text>}
          tooltip="Número de factura, comprobante, o cualquier referencia útil"
        >
          <Input
            placeholder="Ej: Factura #1234, Recibo #567"
            maxLength={50}
          />
        </Form.Item>

        {resultingBalance !== null && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {movementType === 'income' ? 'la caja quedará en ' : 'quedarán '}
            <Text strong style={{ fontFamily: 'ui-monospace, Consolas, monospace' }}>
              {currencySymbol}{resultingBalance.toFixed(2)}
            </Text>
            {movementType === 'income' ? '' : ' en la caja'}
          </Text>
        )}
      </Form>
    </Modal>
  );
};

export default AddCashMovementModal;
